import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { Observable, of } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

// import { EnvironmentService } from '../../../services/enviroment.service';
// import { environment } from '../../../services/global-environment';;

import { environment } from '../../../services/global-environment';
import { DomSanitizer } from '@angular/platform-browser';
import { KeyValuePipe } from '@angular/common'; // 添加 KeyValuePipe
import { FormsModule } from '@angular/forms';

// 保留原有的介面定義
interface RegulationUpdate {
  Month: string;
  TotalCount: number;
}

interface RegulationUpdateResponse {
  items: RegulationUpdate[];
}

interface RegulationDetail {
  id: string;
  lawname: string;
  Doc_Style_LName: string;
  Doc_Style_SName: string;
  PubGov: string;
  PubGovName?: string;
  Date_Published: string;
  GazetteId: string;
  Title: string;
  Explain: string;
  HTMLContent?: string;
  Keyword: string;
  // 新增屬性，用於安全顯示 HTML
  safeHtmlContent?: any;
  topicTypeList?: any[]; // 假設這是法規分類的列表
  read?: boolean; // 新增已讀狀態標記
}

// 簡化樹狀結構的介面
interface YearNode {
  year: string;
  totalCount: number;
  expanded: boolean;
  months: MonthNode[];
}

interface MonthNode {
  month: string; // 格式: "2021-01"
  name: string;  // 顯示名稱: "January"
  count: number;
}

@Component({
  selector: 'app-law-update-news',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatCardModule,
    KeyValuePipe, // 添加這一行
  ],
  templateUrl: './law-update-news.component.html',
  styleUrl: './law-update-news.component.scss',
})
export class LawUpdateNewsComponent implements OnInit {
  private apiUrl = environment.apiUrl;

  years: YearNode[] = [];
  selectedMonth: string | null = null;
  monthDetails: RegulationDetail[] = [];
  isLoadingMonthDetails = false;
  isLoadingTree = false;
  errorMessage = '';

  // 控制彈出窗口顯示
  isPopupVisible = false;
  selectedDetailInfo: RegulationDetail | null = null;

  // 用於存儲所有關鍵字及其數量
  keywordCounts: Map<string, number> = new Map();
  // 用於存儲使用者選擇的關鍵字
  selectedKeywords: Set<string> = new Set();
  // 用於存儲原始的月份詳細資料，便於篩選
  originalMonthDetails: RegulationDetail[] = [];

  // 控制關鍵字篩選區域的收合狀態
  isKeywordCollapsed: boolean = false;

  // 部會篩選相關
  pubGovList: string[] = []; // 所有可用的部會列表
  selectedPubGovs: string[] = []; // 已選擇的部會
  isPubGovDropdownOpen: boolean = false;
  pubGovSearchTerm: string = '';
  filteredPubGovs: string[] = []; // 過濾後的部會列表

  // 法規位階篩選相關
  docStyleList: string[] = []; // 所有可用的法規位階列表
  selectedDocStyles: string[] = []; // 已選擇的法規位階
  isDocStyleDropdownOpen: boolean = false;
  docStyleSearchTerm: string = '';
  filteredDocStyles: string[] = []; // 過濾後的法規位階列表

  // 關鍵字搜尋
  keywordSearchTerm: string = '';

  latestUpdateDate: string | null = null;

  // 搜尋相關屬性
  // selectedPubGovs: string[] = [];

  // 加入法規清單對話框相關屬性
  showAddToRegulationDialog: boolean = false;
  addDialogStep: number = 1; // 步驟：1=選擇主題，2=選擇分類
  companyTopics: string[] = [];
  topicTypes: { type: string; count: number }[] = [];
  selectedAddTopic: string = '';
  selectedAddType: string = '';
  isLoadingTopics: boolean = false;
  isLoadingTypes: boolean = false;
  companyId: string = '';

  // 新增自定義法規分類的輸入
  customTypeInput: string = '';

  // 添加已讀法規ID列表，用於記錄狀態
  readRegulationIds: Set<string> = new Set<string>();

  // 添加一個屬性來追蹤操作中的法規 ID
  processingReadIds: Set<string> = new Set<string>();

  constructor(private http: HttpClient, private sanitizer: DomSanitizer) {
    this.apiUrl = environment.apiUrl;
    this.companyId = environment.companyid;
  }

  ngOnInit(): void {

    // 原有的初始化代碼
    this.getLatestUpdateDate();
    this.initFilterOptions();
  }

  getLatestUpdateDate(): void {
  this.http.get<any>(`${this.apiUrl}/api/regulation_news/get_latest_updatedate`)
    .subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // 格式化日期顯示 (可選)
          this.latestUpdateDate = response.data;
        }
      },
      error: (error) => {
        console.error('獲取最新更新日期失敗:', error);
      }
    });
}


  processUpdateData(updates: RegulationUpdate[]): YearNode[] {
    // 按年份分組
    const yearMap = new Map<string, RegulationUpdate[]>();

    updates.forEach((update) => {
      const year = update.Month.substring(0, 4);
      if (!yearMap.has(year)) {
        yearMap.set(year, []);
      }
      yearMap.get(year)?.push(update);
    });

    // 按年份降序排序
    const sortedYears = Array.from(yearMap.keys()).sort(
      (a, b) => parseInt(b) - parseInt(a)
    );

    // 建立年節點陣列
    return sortedYears.map((year) => {
      const yearUpdates = yearMap.get(year) || [];
      const yearTotal = yearUpdates.reduce(
        (sum, item) => sum + item.TotalCount,
        0
      );

      // 按月份排序
      const sortedMonths = yearUpdates.sort((a, b) =>
        b.Month.localeCompare(a.Month)
      );

      return {
        year,
        totalCount: yearTotal,
        expanded: false,
        months: sortedMonths.map((update) => ({
          month: update.Month,
          name: this.formatMonthName(update.Month),
          count: update.TotalCount,
        })),
      };
    });
  }

  formatMonthName(monthStr: string): string {
    // 直接返回原始月份字符串格式
    return monthStr;
  }

  toggleYear(year: YearNode): void {
    year.expanded = !year.expanded;
  }

  selectMonth(month: string): void {
    this.loadMonthDetails(month);
  }

  // 載入選定月份的詳細資料
  loadMonthDetails(month: string): void {

    this.selectedMonth = month;
    this.isLoadingMonthDetails = true;
    this.monthDetails = [];
    this.keywordCounts.clear();
    this.selectedKeywords.clear();

    // 獲取當前篩選條件
    const selectedPubGovs = this.selectedPubGovs;
    const selectedDocStyles = this.selectedDocStyles;
    const keywordSearchTerm = this.keywordSearchTerm.trim().toLowerCase();

    // 建立請求參數，包含月份和篩選條件
    const requestBody = {
      YM: month,
      selectedPubGovs: selectedPubGovs,
      selectedDocStyles: selectedDocStyles,
      keywordSearchTerm: keywordSearchTerm,
      companyid: this.companyId // 傳遞公司ID
    };

    // 使用 POST 請求並傳遞所有參數
    this.http
      .post<{ items: RegulationDetail[] }>(
        `${this.apiUrl}/api/regulation_news/getupdateYM_detail`,
        requestBody
      )
      .pipe(
        catchError((error) => {
          this.isLoadingMonthDetails = false;
          this.errorMessage = '無法載入詳細資料。請稍後再試。';
          return of({ items: [] });
        })
      )
      .subscribe((response) => {

      console.log('載入月份詳細資料:', response.items);

        this.originalMonthDetails = response.items;
        this.monthDetails = [...this.originalMonthDetails];
        this.isLoadingMonthDetails = false;

        // 統計關鍵字出現次數
        this.countKeywords();
      });
  }

  // 計算關鍵字出現次數
  countKeywords(): void {
    this.keywordCounts.clear();

    this.originalMonthDetails.forEach((detail) => {
      if (detail.Keyword) {
        // 將關鍵字拆分（假設以分號、逗號或分號+空格分隔）
        const keywords = detail.Keyword.split(/[;,]|\s*;\s*/);

        keywords.forEach((keyword) => {
          const trimmedKeyword = keyword.trim();
          if (trimmedKeyword) {
            const count = this.keywordCounts.get(trimmedKeyword) || 0;
            this.keywordCounts.set(trimmedKeyword, count + 1);
          }
        });
      }
    });
  }

  // 切換關鍵字選擇
  toggleKeyword(keyword: string): void {
    if (this.selectedKeywords.has(keyword)) {
      this.selectedKeywords.delete(keyword);
    } else {
      this.selectedKeywords.add(keyword);
    }

    this.filterDetailsByKeywords();
  }

  // 根據選擇的關鍵字篩選詳細資料
  filterDetailsByKeywords(): void {
    // 如果沒有選擇關鍵字，顯示所有資料
    if (this.selectedKeywords.size === 0) {
      this.monthDetails = [...this.originalMonthDetails];
      return;
    }

    // 否則，只顯示包含所選關鍵字的資料
    this.monthDetails = this.originalMonthDetails.filter((detail) => {
      if (!detail.Keyword) return false;

      const detailKeywords = detail.Keyword.split(/[;,]|\s*;\s*/).map((k) =>
        k.trim()
      );

      // 檢查是否包含任何已選關鍵字（多選模式）
      for (const keyword of this.selectedKeywords) {
        if (detailKeywords.includes(keyword)) {
          return true;
        }
      }

      return false;
    });
  }

  // 清除所有已選關鍵字
  clearKeywordFilters(): void {
    this.selectedKeywords.clear();
    this.monthDetails = [...this.originalMonthDetails];
  }

  // 檢查關鍵字是否被選中
  isKeywordSelected(keyword: string): boolean {
    return this.selectedKeywords.has(keyword);
  }

  // 檢視詳細資料
  viewDetailedInfo(detail: RegulationDetail): void {
    // 確保 HTML 內容是安全的
    if (detail.HTMLContent) {
      detail.safeHtmlContent = this.sanitizer.bypassSecurityTrustHtml(
        detail.HTMLContent
      );
    }

    this.selectedDetailInfo = detail;
    this.isPopupVisible = true;
    console.log('查看詳細資料:', detail);
  }

  // 關閉彈出窗口
  closePopup(): void {
    this.isPopupVisible = false;
    this.selectedDetailInfo = null;
  }



  // 隱藏法規
  hideRegulation(detail: RegulationDetail): void {
    console.log('標記法規為已讀:', detail);
    this.markAsRead(detail);
  }

  // 切換關鍵字篩選區域的收合狀態
  toggleKeywordCollapse(): void {
    this.isKeywordCollapsed = !this.isKeywordCollapsed;
  }

  // 初始化篩選選項
  initFilterOptions(): void {
    // 從 API 獲取部會和法規位階列表
    this.http
      .get<{ success: boolean; pubGovs: string[]; docStyles: string[] }>(
        `${this.apiUrl}/api/regulation_news/get_distinct_categories`
      )
      .pipe(
        catchError((error) => {
          console.error('載入篩選選項失敗:', error);
          // 如果 API 調用失敗，使用備用的空陣列或簡單預設值
          return of({
            success: false,
            pubGovs: [],
            docStyles: [],
          });
        })
      )
      .subscribe((response) => {
        if (response.success) {
          // 設置部會列表並排序
          this.pubGovList = response.pubGovs.sort();
          this.filteredPubGovs = [...this.pubGovList];

          // 設置法規位階列表並排序
          this.docStyleList = response.docStyles.sort();
          this.filteredDocStyles = [...this.docStyleList];
        } else {
          // API 回傳失敗或發生錯誤，設置空值
          this.pubGovList = [];
          this.filteredPubGovs = [];
          this.docStyleList = [];
          this.filteredDocStyles = [];
          console.error('載入篩選選項失敗: API 回傳 success=false');
        }
      });
  }

  // 切換下拉選單顯示
  toggleDropdown(type: string): void {
    if (type === 'pubGov') {
      this.isPubGovDropdownOpen = !this.isPubGovDropdownOpen;
      if (this.isPubGovDropdownOpen) {
        this.isDocStyleDropdownOpen = false; // 關閉其他下拉選單
      }
    } else if (type === 'docStyle') {
      this.isDocStyleDropdownOpen = !this.isDocStyleDropdownOpen;
      if (this.isDocStyleDropdownOpen) {
        this.isPubGovDropdownOpen = false; // 關閉其他下拉選單
      }
    }
  }

  // 過濾部會列表
  filterPubGovs(): void {
    const term = this.pubGovSearchTerm.toLowerCase();
    this.filteredPubGovs = this.pubGovList.filter((gov) =>
      gov.toLowerCase().includes(term)
    );
  }

  // 過濾法規位階列表
  filterDocStyles(): void {
    const term = this.docStyleSearchTerm.toLowerCase();
    this.filteredDocStyles = this.docStyleList.filter((style) =>
      style.toLowerCase().includes(term)
    );
  }

  // 切換單個部會選擇
  togglePubGov(gov: string): void {
    if (this.isPubGovSelected(gov)) {
      this.selectedPubGovs = this.selectedPubGovs.filter(
        (item) => item !== gov
      );
    } else {
      this.selectedPubGovs.push(gov);
    }
  }

  // 切換單個法規位階選擇
  toggleDocStyle(style: string): void {
    if (this.isDocStyleSelected(style)) {
      this.selectedDocStyles = this.selectedDocStyles.filter(
        (item) => item !== style
      );
    } else {
      this.selectedDocStyles.push(style);
    }
  }

  // 檢查部會是否已選
  isPubGovSelected(gov: string): boolean {
    return this.selectedPubGovs.includes(gov);
  }

  // 檢查法規位階是否已選
  isDocStyleSelected(style: string): boolean {
    return this.selectedDocStyles.includes(style);
  }

  // 全選/取消全選部會
  toggleAllPubGovs(): void {
    if (this.isAllPubGovsSelected()) {
      this.selectedPubGovs = [];
    } else {
      this.selectedPubGovs = [...this.filteredPubGovs];
    }
  }

  // 全選/取消全選法規位階
  toggleAllDocStyles(): void {
    if (this.isAllDocStylesSelected()) {
      this.selectedDocStyles = [];
    } else {
      this.selectedDocStyles = [...this.filteredDocStyles];
    }
  }

  // 檢查是否已全選部會
  isAllPubGovsSelected(): boolean {
    return (
      this.filteredPubGovs.length > 0 &&
      this.filteredPubGovs.every((gov) => this.selectedPubGovs.includes(gov))
    );
  }

  // 檢查是否已全選法規位階
  isAllDocStylesSelected(): boolean {
    return (
      this.filteredDocStyles.length > 0 &&
      this.filteredDocStyles.every((style) =>
        this.selectedDocStyles.includes(style)
      )
    );
  }

  // 套用搜尋
  applySearch(): void {
    // 取得選取的篩選條件
    const selectedPubGovs = this.selectedPubGovs;
    const selectedDocStyles = this.selectedDocStyles;
    const keywordSearchTerm = this.keywordSearchTerm.trim().toLowerCase();

    // 判斷是否有任何篩選條件
    const hasFilters =
      selectedPubGovs.length > 0 ||
      selectedDocStyles.length > 0 ||
      keywordSearchTerm.length > 0;

    // 如果沒有任何篩選條件，提示使用者輸入條件
    if (!hasFilters) {
      alert('請至少輸入一個搜尋條件');
      return;
    }

    const requestBody = {
      selectedPubGovs: selectedPubGovs,
      selectedDocStyles: selectedDocStyles,
      keywordSearchTerm: keywordSearchTerm
    };

    // console.log('搜尋條件:', requestBody);

    // 使用 POST 請求並傳送搜尋條件
    this.http
      .post<RegulationUpdateResponse>(
        `${this.apiUrl}/api/regulation_news/getupdateYM`,
        requestBody
      )
      .pipe(
        map((response) => this.processUpdateData(response.items)),
        catchError((error) => {
          this.errorMessage = '無法載入資料。請稍後再試。';
          this.isLoadingTree = false;
          return of([]);
        })
      )
      .subscribe((yearNodes) => {
        this.years = yearNodes;
        this.isLoadingTree = false;
      });


  }

  // 清除搜尋條件
  clearSearch(): void {
    this.selectedPubGovs = [];
    this.selectedDocStyles = [];
    this.keywordSearchTerm = '';

    // 如果有選擇月份，重新載入原始資料
    if (this.selectedMonth) {
      this.monthDetails = [...this.originalMonthDetails];
    }
  }

  // 根據搜尋條件過濾月份詳情
  filterDetailsBySearchCriteria(): void {
    // 如果沒有任何篩選條件，顯示所有資料
    if (
      this.selectedPubGovs.length === 0 &&
      this.selectedDocStyles.length === 0 &&
      !this.keywordSearchTerm.trim()
    ) {
      this.monthDetails = [...this.originalMonthDetails];
      return;
    }

    // 應用過濾條件
    this.monthDetails = this.originalMonthDetails.filter((detail) => {
      // 部會篩選
      if (
        this.selectedPubGovs.length > 0 &&
        !this.selectedPubGovs.includes(detail.PubGov)
      ) {
        return false;
      }

      // 法規位階篩選
      if (
        this.selectedDocStyles.length > 0 &&
        !this.selectedDocStyles.includes(detail.Doc_Style_LName)
      ) {
        return false;
      }

      // 關鍵字搜尋
      if (this.keywordSearchTerm.trim()) {
        const keyword = this.keywordSearchTerm.trim().toLowerCase();
        // 搜尋多個欄位
        return (
          (detail.Title && detail.Title.toLowerCase().includes(keyword)) ||
          (detail.lawname && detail.lawname.toLowerCase().includes(keyword)) ||
          (detail.Explain && detail.Explain.toLowerCase().includes(keyword)) ||
          (detail.Keyword && detail.Keyword.toLowerCase().includes(keyword))
        );
      }

      return true;
    });
  }

  // 快速篩選方法
// 快速篩選方法
applyQuickFilter(event: Event): void {
  // 正確的類型轉換
  const selectElement = event.target as HTMLSelectElement;
  const filterType = selectElement.value;

  // 先清除所有選擇
  this.selectedPubGovs = [];
  this.selectedDocStyles = [];

  // 根據選擇套用不同的篩選條件
  switch (filterType) {
    case 'occupational_safety':
      // 職業安全 - 勾選勞動部和相關法規位階
      this.selectedPubGovs = ['勞動部'];
      this.selectedDocStyles = ['法規', '行政規則'];
      break;

    case 'environmental_protection':
      // 環境保護 - 勾選環境部、環保署和相關法規位階
      this.selectedPubGovs = ['環境部', '環保署'];
      this.selectedDocStyles = ['法規', '行政規則'];
      break;

    case 'health_welfare':
      // 衛生福利 - 勾選衛生福利部和相關法規位階
      this.selectedPubGovs = ['衛生福利部'];
      this.selectedDocStyles = ['法規', '行政規則'];
      break;

    default:
      // 如果選擇「請選擇」或其他值，不做任何操作
      break;
  }

  // 如果有選擇快速篩選條件，則自動應用搜尋
  if (filterType) {
    console.log('套用快速篩選:', filterType, {
      部會: this.selectedPubGovs,
      法規位階: this.selectedDocStyles
    });

    // 可選：自動點擊搜尋按鈕
    // this.applySearch();
  }
}

// 開啟添加到法規清單對話框
openAddToRegulationListDialog(detail: RegulationDetail): void {

  this.selectedDetailInfo = detail;
  this.showAddToRegulationDialog = true;
  this.addDialogStep = 1;
  this.selectedAddTopic = '';
  this.selectedAddType = '';
  this.loadCompanyTopics();

  // 可選: 輸出調試信息，確認選擇的法規和現有主題
  console.log('選擇的法規:', detail.lawname);
  console.log('現有主題列表:', detail.topicTypeList || []);
}

// 關閉對話框
closeAddToRegulationListDialog(): void {
  this.showAddToRegulationDialog = false;

 this.selectedDetailInfo = null;

}

// 載入公司所有主題
loadCompanyTopics(): void {
  this.isLoadingTopics = true;
  this.companyTopics = [];

  this.http.post<any>(`${this.apiUrl}/api/regulation/get_topics`, {
    companyid: this.companyId
  }).subscribe({
    next: (response: any) => {
      this.isLoadingTopics = false;
      if (response && response.success) {
        this.companyTopics = response.data.map((item: any) => item.topic) || [];
      } else {
        console.error('載入主題列表失敗:', response.message || '未知錯誤');
      }
    },
    error: (err) => {
      this.isLoadingTopics = false;
      console.error('Failed to load topics:', err);
    }
  });
}

// 選擇主題後載入該主題的法規分類
selectAddTopic(topic: string): void {
  // 檢查此主題是否已添加過該法規
  if (this.isTopicInDetailList(topic)) {
    console.log(`主題「${topic}」已包含此法規，無法重複添加`);
    return; // 如果已添加，則不執行選擇動作
  }

  this.selectedAddTopic = topic;
}

// 進入下一步驟
proceedAddToRegulationList(): void {
  if (this.addDialogStep === 1) {
    // 檢查是否已選擇主題
    if (!this.selectedAddTopic) {
      alert('請先選擇要添加的主題');
      return;
    }

    // 檢查選擇的主題是否已包含此法規
    if (this.isTopicInDetailList(this.selectedAddTopic)) {
      alert(`該法規已在「${this.selectedAddTopic}」主題中存在，請選擇其他主題或取消操作`);
      return;
    }

    // 從步驟1進入步驟2，載入所選主題的法規分類
    this.addDialogStep = 2;
    this.loadTopicTypes();
  } else {
    // 步驟2完成後，執行添加到法規清單的操作
    this.addToRegulationList();
  }
}

// 載入主題下的法規分類
loadTopicTypes(): void {
  this.isLoadingTypes = true;
  this.topicTypes = [];

  this.http.post<any>(`${this.apiUrl}/api/regulation/get_law_types`, {
    companyid: this.companyId,
    topic: this.selectedAddTopic
  }).subscribe({
    next: (response: any) => {
      this.isLoadingTypes = false;
      if (response && response.success) {
        console.log('法規類型回應:', response.data);

        // 處理回應是字串陣列的情況
        if (Array.isArray(response.data)) {
          // 假設 response.data 是直接的字串陣列
          // 為每個類型創建一個物件，目前沒有實際數量，先設為1
          this.topicTypes = response.data.map((type: string) => ({
            type: type
          }));
        } else {
          // 如果回應結構不是預期的字串陣列，輸出警告
          console.warn('回應資料結構不是預期的字串陣列:', response.data);
          this.topicTypes = [];
        }
      } else {
        console.error('載入法規類型失敗:', response.message || '未知錯誤');
      }
    },
    error: (err) => {
      this.isLoadingTypes = false;
      console.error('Failed to load regulation types:', err);
    }
  });
}

// 選擇法規分類 (更新為同時支援現有分類和自定義分類)
selectAddType(type: string): void {
  // 確保傳入的分類不為空
  if (!type.trim()) return;

  // 檢查是否是自訂分類（來自輸入框）
  const isCustomType = type === this.customTypeInput && type.trim() !== '';

  // 如果是自訂分類，新增到類型列表中的最前端
  if (isCustomType && !this.typeExistsInList(type)) {
    this.topicTypes.unshift({
      type: type.trim(),
      count: 0 // 新增的自訂分類，初始數量為0
    });
  }

  this.selectedAddType = type.trim();

  // 如果選擇了既有分類，清空輸入框
  if (type !== this.customTypeInput) {
    this.customTypeInput = '';
  }
}

// 檢查類型是否已存在於列表中
private typeExistsInList(type: string): boolean {
  if (!type || !this.topicTypes) return false;
  return this.topicTypes.some(item => item.type.toLowerCase() === type.toLowerCase().trim());
}



// 重置對話框狀態
backToTopicSelection(): void {
  this.addDialogStep = 1;
  this.selectedAddType = '';
  this.customTypeInput = ''; // 清空自定義輸入
}

// 添加到法規清單
addToRegulationList(): void {
  // 如果輸入框有值，優先使用輸入框的值作為分類
  if (this.customTypeInput && this.customTypeInput.trim()) {
    this.selectedAddType = this.customTypeInput.trim();
  }

  // 檢查是否有選擇主題和法規分類
  if (!this.selectedAddTopic || !this.selectedAddType) {
    alert('請選擇主題和法規分類');
    return;
  }

  // 建立添加法規的請求資料
  const requestData = {
    companyid: this.companyId,
    topic: this.selectedAddTopic,
    type: this.selectedAddType,
    newsId: this.selectedDetailInfo?.id || '',
    lawname: this.selectedDetailInfo?.lawname || '',
  };

  console.log('添加法規請求資料:', requestData);

  this.http.post<any>(`${this.apiUrl}/api/regulation/add_to_law_list`, requestData).subscribe({
    next: (response: any) => {
      if (response && response.success) {
        // 成功添加後，更新原本的資料
        if (this.selectedDetailInfo) {
          // 確保 topicTypeList 存在
          if (!this.selectedDetailInfo.topicTypeList) {
            this.selectedDetailInfo.topicTypeList = [];
          }

          // 添加新的主題和分類到 topicTypeList 中
          this.selectedDetailInfo.topicTypeList.push({
            topic: this.selectedAddTopic,
            type: this.selectedAddType,
            law_id: response.law_id || '', // 如果 API 回傳了新增的 law_id，則使用它
            timestamp: new Date().toISOString() // 記錄添加時間
          });

          // 在原始月份詳情中也找到對應的法規並更新
          const foundDetail = this.originalMonthDetails.find(
            detail => detail.id === this.selectedDetailInfo?.id
          );

          if (foundDetail) {
            // 確保 topicTypeList 存在
            if (!foundDetail.topicTypeList) {
              foundDetail.topicTypeList = [];
            }

            // 將新的主題和分類添加到原始資料中
            foundDetail.topicTypeList.push({
              topic: this.selectedAddTopic,
              type: this.selectedAddType,
              law_id: response.law_id || '',
              timestamp: new Date().toISOString()
            });
          }

          console.log('已更新法規的主題資訊:', this.selectedDetailInfo.topicTypeList);
        }

        alert(`已成功將「${this.selectedDetailInfo?.lawname}」添加到「${this.selectedAddTopic}」主題下的「${this.selectedAddType}」分類。`);
        this.closeAddToRegulationListDialog();
      } else {
        alert('添加法規失敗: ' + (response.message || '未知錯誤'));
      }
    },
    error: (err) => {
      console.error('添加法規時發生錯誤:', err);
      alert('添加法規失敗: ' + (err.message || '網路錯誤'));
    }
  });
}

// 添加此方法來計算不重複的 topic 數量
getDistinctTopicsCount(topicTypeList: any[] | undefined): number {
  if (!topicTypeList || !Array.isArray(topicTypeList) || topicTypeList.length === 0) {
    return 0;
  }

  // 使用 Set 來取得不重複的 topic 值
  const distinctTopics = new Set(topicTypeList.map(item => item.topic));
  return distinctTopics.size;
}

// 添加函數來判斷某个主題是否已存在於當前法規的主題列表中
isTopicInDetailList(topic: string): boolean {
  // 確保有被選擇的法規詳情和topicTypeList
  if (!this.selectedDetailInfo || !this.selectedDetailInfo.topicTypeList || !Array.isArray(this.selectedDetailInfo.topicTypeList)) {
    return false;
  }

  // 檢查該主題是否存在於法規的主題清單中 - 大小寫不敏感比較
  return this.selectedDetailInfo.topicTypeList.some(item =>
    item.topic.toLowerCase() === topic.toLowerCase()
  );
}



/**
 * 將 HTML 內容中的相對連結轉換為絕對連結
 * @param detailInfo 法規詳細資訊
 * @returns 轉換後的 HTML 內容
 */
transformHtmlContent(detailInfo: any): string {
  if (!detailInfo || !detailInfo.HTMLContent) {
    return '';
  }

  let htmlContent = detailInfo.HTMLContent;

  // 如果有 GazetteHTML，從中取得基礎 URL
  if (detailInfo.GazetteHTML) {
    try {
      // 從 GazetteHTML URL 取得基礎路徑
      // 例如 https://gazette.nat.gov.tw/EG_FileManager/eguploadpub/eg031096/ch08/type2/gov82/num55/Eg.htm
      // 轉換為 https://gazette.nat.gov.tw/EG_FileManager/eguploadpub/eg031096/ch08/type2/gov82/num55/
      const urlString = detailInfo.GazetteHTML;
      const lastSlashIndex = urlString.lastIndexOf('/');
      const baseUrl = urlString.substring(0, lastSlashIndex + 1);

      // 使用正則表達式尋找相對連結並替換為絕對連結
      // 替換 href="images/file.pdf" 為 href="baseUrl/images/file.pdf"
      htmlContent = htmlContent.replace(
        /href="(?!http|https|ftp|\/\/|#)([^"]*)"/gi,
        (_match: string, p1: string) => `href="${baseUrl}${p1}" target="_blank"`
      );

      // 替換圖片的相對連結
      htmlContent = htmlContent.replace(
        /src="(?!http|https|ftp|\/\/|data:)([^"]*)"/gi,
        (_match: string, p1: string) => `src="${baseUrl}${p1}"`
      );
    } catch (error) {
      console.error('處理 HTML 內容時發生錯誤:', error);
    }
  }

  return htmlContent;
}

// 標記法規為已讀
markAsRead(detail: RegulationDetail | null): void {
  if (!detail || !detail.id) return;

  // 如果此法規正在處理中，不執行重複操作
  if (this.processingReadIds.has(detail.id)) {
    return;
  }

  // 標記此法規為正在處理
  this.processingReadIds.add(detail.id);

  // 準備 API 請求參數
  const requestData = {
    companyid: this.companyId,
    newsid: detail.id,
    lawname: detail.lawname || '未知法規',
    userId: 'system' // 如果有用戶 ID 系統，這裡可以設置
  };

  // 根據法規當前的已讀狀態執行相應的 API 請求
  const isCurrentlyRead = detail.read === true; // 使用 API 返回的 read 欄位
  const apiEndpoint = isCurrentlyRead
    ? `${this.apiUrl}/api/regulation/news/delete_readed`
    : `${this.apiUrl}/api/regulation/news/add_readed`;

  this.http.post<any>(apiEndpoint, requestData).subscribe({
    next: (response) => {
      if (response.success) {
        // API 請求成功，直接更新當前物件的已讀狀態
        detail.read = !isCurrentlyRead;

        console.log(`已${isCurrentlyRead ? '取消' : ''}將「${detail.lawname}」標記為已讀`);

        // 如果是在彈出視窗中的法規，同時更新月份詳情列表中的相應項目
        if (this.selectedDetailInfo && this.selectedDetailInfo.id === detail.id) {
          this.selectedDetailInfo.read = detail.read;
        }

        // 同步更新月份詳情列表中的相同法規
        const foundInList = this.monthDetails.find(item => item.id === detail.id);
        if (foundInList) {
          foundInList.read = detail.read;
        }

        // 更新 UI 狀態 (複製陣列以觸發變更檢測)
        this.monthDetails = [...this.monthDetails];
      } else {
        // API 回報失敗但請求成功
        console.error(`操作失敗: ${response.message || '未知錯誤'}`);
      }
    },
    error: (error) => {
      // 請求發生錯誤
      console.error('標記法規已讀狀態時發生錯誤:', error);

      // 如果是找不到記錄的錯誤（刪除時），可以特別處理
      if (error.status === 404 && isCurrentlyRead) {
        // 雖然 API 回傳 404，但我們還是在前端更新狀態（因為用戶就是想取消標記）
        detail.read = false;

        // 同步更新月份詳情列表中的相同法規
        if (this.selectedDetailInfo && this.selectedDetailInfo.id === detail.id) {
          this.selectedDetailInfo.read = false;
        }

        const foundInList = this.monthDetails.find(item => item.id === detail.id);
        if (foundInList) {
          foundInList.read = false;
        }

        // 更新 UI 狀態
        this.monthDetails = [...this.monthDetails];
      }
    },
    complete: () => {
      // 無論成功或失敗，完成後都移除處理中狀態
      this.processingReadIds.delete(detail.id);
    }
  });
}

// 檢查法規是否已讀 - 直接使用 API 返回的 read 欄位
isMarkedAsRead(detail: RegulationDetail | null): boolean {
  if (!detail) return false;
  return detail.read === true;
}
}
